import { ParamMap } from '@angular/router';

import { AirValidationBySectorResponse, AirValidationResponse } from '@app/entities/air-validation.response';
import {
  AssistanceBookPayload, AssistanceReservationInfo, AssistanceSearchPayload, RouteType
} from '@shared/travel-assistance/entities/booking-assistance';
import { Travelers } from '@booking/entities/booking-arrangement';
import { formatStrinWithTimeZoneDateByRegex } from '@ux/mappers/date.mapper';
import { travelerAgeInYears } from '@pax/mappers/traveler-age.mapper';
import { User } from '@app/modules/core/interfaces/user';
import { languageFormat } from '@ux/mappers/language.mapper';
import { environment as ENV } from '@environment';
import { SearchBarState } from '@shared/base-search-bar/store/state/search-bar.state';
import { BOOK_SESSION_TOKEN_PARAM } from '@booking/constants/booking-routes.constants';
import { HotelValidationResponse } from '@booking/entities/hotel-validation.response';
import { VehicleValidateResponse } from '@autos/entities/vehicle-validate.response';
import { TravelerPaxResponse } from '@reservation/pax/entities/traveler-pax';

export function assistanceInfoByAirValidation(
  validation: AirValidationResponse, travelers: TravelerPaxResponse[]
): AssistanceReservationInfo {
  const allSectors = validation?.TripOption?.Sectors?.length;
  if (allSectors) {
    const sectors = validation.TripOption.Sectors;
    const departure = sectors[0];
    const arrival = sectors[allSectors - 1];
    const departureFlightRef = departure.Flights[0];
    const arrivalByDepartureFlightRef = departure.Flights[departure.Flights.length - 1];
    const departureByArrivalFlightRef = arrival.Flights[arrival.Flights.length - 1];
    const reservationDate = departureFlightRef?.DepartureDate ?
      new Date(formatStrinWithTimeZoneDateByRegex(departureFlightRef.DepartureDate)) :
      new Date();
    return assistanceObject(
      departureFlightRef.DepartureAirport,
      arrivalByDepartureFlightRef.ArrivalAirport,
      departureFlightRef.DepartureDate,
      departureByArrivalFlightRef.ArrivalDate,
      travelers,
      reservationDate,
      getRouteType(allSectors));
  } else return null;
}

export function assistanceInfoByAirBySectorValidation(
  validation: AirValidationBySectorResponse, travelers: TravelerPaxResponse[]
): AssistanceReservationInfo {
  const firstSector = validation?.AirValidateResponse[0];
  const lastSector = validation?.AirValidateResponse[validation?.AirValidateResponse?.length - 1];
  const departure = firstSector?.TripOption?.Sectors[0];
  const arrival = lastSector?.TripOption?.Sectors[lastSector?.TripOption?.Sectors?.length - 1];
  const sectors = validation?.AirValidateResponse.length;
  const numSectors = sectors > 1 ? sectors : firstSector?.TripOption?.Sectors?.length || 0;
  if (departure && arrival) {
    const departureFlightRef = departure.Flights[0];
    const arrivalByDepartureFlightRef = departure.Flights[departure.Flights.length - 1];
    const departureByArrivalFlightRef = arrival.Flights[arrival.Flights.length - 1];
    const reservationDate = departureFlightRef?.DepartureDate ?
      new Date(formatStrinWithTimeZoneDateByRegex(departureFlightRef.DepartureDate)) :
      new Date();
    return assistanceObject(
      departureFlightRef.DepartureAirport,
      arrivalByDepartureFlightRef.ArrivalAirport,
      departureFlightRef.DepartureDate,
      departureByArrivalFlightRef.ArrivalDate,
      travelers,
      reservationDate,
      getRouteType(numSectors));
  } else return null;
}

export function assistanceInfoByHotelValidation(
  validation: HotelValidationResponse, travelers: TravelerPaxResponse[]
): AssistanceReservationInfo {
  const reservation = validation.HotelReservation;
  if (reservation) {
    const reservationDate = reservation?.Checkin ?
      new Date(formatStrinWithTimeZoneDateByRegex(reservation.Checkin)) : new Date();
    return assistanceObject(
      reservation.Hotel?.CityCode,
      reservation.Hotel?.CityCode,
      reservation.Checkin,
      reservation.Checkout,
      travelers,
      reservationDate);
  } else return null;
}

export function assistanceInfoByVehicleValidation(
  validation: VehicleValidateResponse, travelers: TravelerPaxResponse[]
): AssistanceReservationInfo {
  const reservation = validation.VehicleOption;
  if (reservation) {
    const reservationDate = reservation?.PickUp?.Time ?
      new Date(formatStrinWithTimeZoneDateByRegex(reservation.PickUp.Time)) : new Date();
    return assistanceObject(
      reservation.PickUp?.DestinationCode,
      reservation.DropOff?.DestinationCode,
      reservation.PickUp?.Time,
      reservation.DropOff?.Time,
      travelers,
      reservationDate);
  } else return null;
}

export function assistanceReservationPayload(
  info: AssistanceReservationInfo, currencyCode: string,
  searchData: SearchBarState, user: User, params: ParamMap
): AssistanceSearchPayload {
  return {
    AssistanceReservation: info,
    RequestSettings: {
      SalesChanelCode: searchData.salesChannel,
      BusinessUnitCode: ENV.business_unit_code,
      BranchCode: searchData.branchCode,
      Language: languageFormat(ENV.countryInfo.locale),
      PromoCode: user.promo_code || null
    },
    CurrencyCode: currencyCode,
    SessionToken: params.get(BOOK_SESSION_TOKEN_PARAM),
  };
}

export function assistanceBookPayload(
  searchId: string, travelers: Travelers[], assistanceId: number
): AssistanceBookPayload {
  return {
    BookRequest: {
      OptionId: assistanceId,
      Passengers: travelers
    },
    SearchId: searchId,
  };
}

export function getRouteType(sectors: number): RouteType {
  if (!Number.isInteger(sectors) || sectors < 1) {
    return RouteType.ROUNDTRIP;
  }
  switch (sectors) {
    case 1:
      return RouteType.ONEWAY;
    case 2:
      return RouteType.ROUNDTRIP;
    default:
      return RouteType.MULTIPLE_DESTINATION;
  }
}

const assistanceObject = (
  OriginCityCode: string,
  DestinationCityCode: string, 
  StartDate: string,
  EndDate: string,
  travelers: TravelerPaxResponse[],
  reservationDate: Date,
  routeType?: RouteType
): AssistanceReservationInfo => {
  return {
    OriginCityCode,
    DestinationCityCode,
    StartDate,
    EndDate,
    RouteType: routeType,
    PassengerAges: travelers.map(traveler => {
      const age = travelerAgeInYears(traveler.dob, reservationDate);
      return age ? age : 1;
    })
  };
}

